/**
 * Display formatters shared across views.
 * Same behavior as the single-file app's helpers — `fmt$`, `fmt$0`, `fmtN`,
 * `fmtDate`, `todayISO`, `sum`, plus the dashboard `kpi` / `snap` chip helpers.
 */

/* These accept `any` because they're called with raw `Row[K]` values throughout
   the views, which TypeScript types as `any` anyway. Inside each we coerce
   safely before display. */

/** Money with cents. Returns "—" for null / NaN. */
export const fmt$ = (n: any): string =>
  n == null || isNaN(+n) ? "—"
    : "$" + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Money rounded to whole dollars. */
export const fmt$0 = (n: any): string =>
  n == null || isNaN(+n) ? "—" : "$" + Math.round(Number(n)).toLocaleString();

/** Plain number with locale grouping. */
export const fmtN = (n: any): string =>
  n == null || isNaN(+n) ? "—" : Number(n).toLocaleString();

/** ISO-8601 date (YYYY-MM-DD), tolerant of string/Date inputs. */
export const fmtDate = (d: any): string => {
  if (!d) return "";
  if (typeof d === "string") return d.slice(0, 10);
  try { return new Date(d).toISOString().slice(0, 10); } catch { return ""; }
};

/** Today as YYYY-MM-DD. */
export const todayISO = (): string => new Date().toISOString().slice(0, 10);

/** Sum a numeric field over an array. */
export const sum = <T>(arr: T[], fn: (x: T) => any = (x) => x as any): number =>
  arr.reduce<number>((s, x) => s + (Number(fn(x)) || 0), 0);

type KpiTone = "neutral" | "violet" | "blue" | "green" | "red" | "amber";

/** Render a KPI tile. Most KPIs should stay "neutral" so the eye can rest;
 *  reserve color for sign-based numbers (green/red) or the one figure on a
 *  page that's really the answer. */
export function kpi(label: string, value: string, tone: KpiTone = "neutral"): string {
  const toneClass: Record<Exclude<KpiTone, "neutral">, string> = {
    violet: "bg-gradient-to-br from-indigo-500/20 to-violet-500/10 text-indigo-200",
    blue:   "bg-gradient-to-br from-cyan-500/20 to-sky-500/10 text-cyan-200",
    green:  "bg-gradient-to-br from-emerald-500/20 to-green-500/10 text-emerald-200",
    red:    "bg-gradient-to-br from-rose-500/25 to-red-500/10 text-rose-200",
    amber:  "bg-gradient-to-br from-amber-500/20 to-yellow-500/10 text-amber-200",
  };
  const extra = tone === "neutral" ? "" : " " + toneClass[tone];
  return `<div class="panel p-3${extra}"><div class="text-xs text-[var(--muted)]">${label}</div><div class="text-xl font-bold mt-1 stat-num">${value}</div></div>`;
}

/** Render a label/value snap row inside an inventory panel. */
/** Compact stat bar — the shared topline for every tab (2026-07-11).
 *  One panel, label-over-value cells: ~40% shorter than the old kpi() tile
 *  grid but keeps the same information and tone coloring. kpi() tiles remain
 *  for the dashboard widgets, where the larger format is intentional. */
export function kpiBar(items: Array<{ label: string; value: string; tone?: KpiTone }>): string {
  return `<div class="panel px-4 py-2.5 mb-4 flex flex-wrap items-center gap-x-8 gap-y-2">${items.map(it => {
    const toneCls = it.tone === "green" ? " text-emerald-300"
      : it.tone === "red"    ? " text-rose-300"
      : it.tone === "amber"  ? " text-amber-300"
      : it.tone === "blue"   ? " text-cyan-300"
      : it.tone === "violet" ? " text-indigo-300"
      : "";
    return `<div class="min-w-0">
      <div class="text-xs text-[var(--muted)] whitespace-nowrap">${it.label}</div>
      <div class="stat-num text-lg font-bold leading-tight${toneCls}">${it.value}</div>
    </div>`;
  }).join("")}</div>`;
}

export function snap(k: string, v: string | number): string {
  return `<div class="flex justify-between"><span class="text-[var(--muted)]">${k}</span><span class="stat-num">${v}</span></div>`;
}

/** Convert a stored Current FMV (per-LOT total) into a per-unit value for display in
 *  the Sealed/Card edit form. Returns "" when nothing is set. */
export function unitFMVForForm(row: Record<string, any>): string {
  if (!row || row["Current FMV"] == null || row["Current FMV"] === "") return "";
  const total = Number(row["Current FMV"]) || 0;
  const qty   = Number(row["Qty"]) || 0;
  if (qty > 0) return (+(total / qty).toFixed(2)).toString();
  return total ? total.toString() : "";
}
